import { musicMap, type MusicKey } from "./musicMap";

/**
 * Singleton background-music controller.
 *
 * - One active track at a time. Switching crossfades.
 * - Reuses a single HTMLAudioElement per key; never stacks.
 * - Idempotent: `play(currentKey)` is a no-op.
 * - Respects browser autoplay: caller must unlock via `unlock()` after a
 *   user gesture.
 * - Supports ducking under narration without losing playback state.
 */
class MusicController {
  private pool = new Map<MusicKey, HTMLAudioElement>();
  private currentKey: MusicKey | null = null;
  private unlocked = false;
  private masterVolume = 1; // user-set multiplier 0..1
  private muted = false;
  private duckFactor = 1;   // 1 = no duck
  private targets = new Map<HTMLAudioElement, number>(); // intended final volume
  private fadeTimers = new Map<HTMLAudioElement, number>();
  // Ref-counted ducking so overlapping sources (video + Lumi voice) don't
  // unduck while another narrator is still active.
  private duckStack: number[] = [];

  private log(...args: unknown[]) {
    if (import.meta.env?.DEV) {
      console.debug("[music]", ...args);
    }
  }

  unlock() {
    this.unlocked = true;
    this.applyVolumes();

    // A scene can be selected before the first gesture. When the browser unlock
    // arrives, actually start that pending scene instead of waiting for a
    // rerender or a settings toggle.
    if (!this.muted && this.currentKey) {
      const key = this.currentKey;
      const el = this.getOrCreate(key);
      const intended = this.targets.get(el) ?? musicMap[key].defaultVolume;
      this.targets.set(el, intended);
      try {
        el.muted = false;
        el.loop = musicMap[key].loop;
        if (el.paused) {
          el.volume = 0;
          const p = el.play();
          if (p && typeof p.catch === "function") p.catch(() => {});
          this.fadeTo(el, intended * this.masterVolume * this.duckFactor, musicMap[key].fadeInMs);
        }
      } catch { /* ignore */ }
    }
  }
  isUnlocked() { return this.unlocked; }

  setMasterVolume(v: number) {
    this.masterVolume = Math.max(0, Math.min(1, v));
    this.applyVolumes();
  }
  getMasterVolume() { return this.masterVolume; }

  setMuted(m: boolean) {
    this.muted = m;
    if (m) {
      // Pause everything immediately
      this.pool.forEach((el) => { el.pause(); });
    } else if (this.currentKey) {
      // Resume the current track at intended volume.
      const key = this.currentKey;
      const el = this.getOrCreate(key);
      const intended = this.targets.get(el) ?? musicMap[key].defaultVolume;
      this.targets.set(el, intended);
      try {
        el.muted = false;
        el.loop = musicMap[key].loop;
        el.volume = 0;
        const p = el.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
      } catch { /* ignore */ }
      const target = intended * this.masterVolume * this.duckFactor;
      this.fadeTo(el, target, musicMap[key].fadeInMs);
    }
  }
  isMuted() { return this.muted; }

  duck(factor = 0.5, ms = 250) {
    this.duckFactor = Math.max(0, Math.min(1, factor));
    this.applyVolumes(ms);
  }
  unduck(ms = 400) {
    this.duckFactor = 1;
    this.applyVolumes(ms);
  }

  /** Ref-counted duck. Returns an `unduck` fn that pops this duck off the stack. */
  pushDuck(factor = 0.4, ms = 250) {
    const f = Math.max(0, Math.min(1, factor));
    this.duckStack.push(f);
    this.duckFactor = Math.min(this.duckFactor, f);
    this.applyVolumes(ms);
    let popped = false;
    return (unduckMs = 400) => {
      if (popped) return;
      popped = true;
      // Remove the first matching factor.
      const idx = this.duckStack.indexOf(f);
      if (idx >= 0) this.duckStack.splice(idx, 1);
      this.duckFactor = this.duckStack.length ? Math.min(...this.duckStack) : 1;
      this.applyVolumes(unduckMs);
    };
  }

  /** Switch to `key` with a crossfade. No-op if already playing this key. */
  play(key: MusicKey, opts?: { volume?: number; fadeMs?: number }) {
    if (this.muted) { this.currentKey = key; return; }
    if (!this.unlocked) { this.currentKey = key; return; }
    if (this.currentKey === key) {
      // Just ensure it's actually playing (could have been paused)
      const el = this.pool.get(key);
      if (el) {
        try { el.muted = false; } catch { /* ignore */ }
        if (el.paused) { el.play().catch(() => {}); }
      }
      this.log("play (noop, already active):", key);
      // Even on no-op, sweep any orphan tracks that might still be
      // mid-fade from a prior rapid switch so they cannot stack.
      this.killOrphans(el);
      return;
    }
    const prevKey = this.currentKey;
    this.currentKey = key;

    const entry = musicMap[key];
    const target = (opts?.volume ?? entry.defaultVolume) * this.masterVolume * this.duckFactor;
    const fadeInMs = opts?.fadeMs ?? entry.fadeInMs;

    const el = this.getOrCreate(key);
    this.targets.set(el, (opts?.volume ?? entry.defaultVolume));
    // If a previous fade-out for this same element is in flight (from a stop()
    // followed by a rapid play of the same key), clear it so the pending
    // pause() callback can't kill the freshly-started playback.
    const existingTimer = this.fadeTimers.get(el);
    if (existingTimer) {
      window.clearInterval(existingTimer);
      this.fadeTimers.delete(el);
    }
    try {
      el.muted = false;
      el.loop = entry.loop;
      // Only reset playhead if the element is not already running, so
      // re-entrant play() of the same logical scene doesn't audibly restart.
      if (el.paused) {
        el.currentTime = 0;
        el.volume = 0;
      }
      const p = el.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    } catch { /* ignore */ }
    this.fadeTo(el, target, fadeInMs);
    this.log("play:", key, "prev:", prevKey);

    // Hard guarantee: at most one audible track. Fade out every other
    // pool element that is currently playing, not just the most recent
    // `prevKey`. Rapid A → B → C switches previously left A still
    // fading while C started — audibly layered. Now we sweep them all.
    this.killOrphans(el);
  }

  /**
   * Fade out and pause every pool element other than `keep`. Idempotent
   * and cheap to call on every play(). Uses a shortened fade so orphans
   * cannot remain audible behind the new track.
   */
  private killOrphans(keep: HTMLAudioElement | null) {
    this.pool.forEach((el) => {
      if (el === keep) return;
      // Clear the intended-volume entry FIRST so any concurrent
      // applyVolumes() call (triggered by duck/unduck/mute/master-volume
      // changes — which fire during instructor voice moments on module
      // summary screens) cannot fade this orphan back up while we're
      // trying to fade it out. Without this, moduleCompletion + the
      // previous scene (simWalletFlow / quizFocus / reflectionState)
      // could both stay audible during the crossfade window.
      this.targets.delete(el);
      if (el.paused && el.volume === 0) return;
      const fadeMs = Math.min(400, el.volume > 0 ? 400 : 0);
      this.fadeTo(el, 0, fadeMs, () => {
        try { el.pause(); el.currentTime = 0; } catch { /* ignore */ }
      });
    });
  }

  /** Fade out and pause current track. */
  stop(opts?: { fadeMs?: number }) {
    const key = this.currentKey;
    if (!key) {
      // Even with no active key, ensure no orphaned element is still
      // bleeding sound from a prior rapid switch.
      this.killOrphans(null);
      return;
    }
    this.currentKey = null;
    const el = this.pool.get(key);
    if (!el) { this.killOrphans(null); return; }
    const fadeMs = opts?.fadeMs ?? musicMap[key].fadeOutMs;
    // Same reasoning as killOrphans: drop the intended-volume entry so
    // an incoming duck/unduck/volume change can't re-inflate this
    // element mid-fade and leave it audible under the next scene.
    this.targets.delete(el);
    this.fadeTo(el, 0, fadeMs, () => {
      try { el.pause(); } catch { /* ignore */ }
    });
    // Belt-and-suspenders: also sweep every other element.
    this.killOrphans(el);
    this.log("stop:", key);
  }

  getCurrent(): MusicKey | null { return this.currentKey; }

  private getOrCreate(key: MusicKey): HTMLAudioElement {
    let el = this.pool.get(key);
    if (el) return el;
    el = new Audio(musicMap[key].url);
    el.preload = "auto";
    el.crossOrigin = "anonymous";
    el.loop = musicMap[key].loop;
    el.volume = 0;
    this.pool.set(key, el);
    return el;
  }

  private applyVolumes(fadeMs = 200) {
    this.pool.forEach((el) => {
      const intended = this.targets.get(el);
      if (intended === undefined) return;
      const target = intended * this.masterVolume * this.duckFactor;
      if (el.paused) {
        // Set immediately so the next play() starts at the right level.
        try { el.volume = Math.max(0, Math.min(1, target)); } catch { /* ignore */ }
      } else {
        this.fadeTo(el, target, fadeMs);
      }
    });
  }

  private fadeTo(el: HTMLAudioElement, target: number, ms: number, onDone?: () => void) {
    const existing = this.fadeTimers.get(el);
    if (existing) { window.clearInterval(existing); this.fadeTimers.delete(el); }
    const clamped = Math.max(0, Math.min(1, target));
    const start = el.volume;
    if (ms <= 0 || Math.abs(start - clamped) < 0.001) {
      try { el.volume = clamped; } catch { /* ignore */ }
      onDone?.();
      return;
    }
    const steps = Math.max(4, Math.round(ms / 40));
    let i = 0;
    const id = window.setInterval(() => {
      i += 1;
      const t = i / steps;
      const v = start + (clamped - start) * t;
      try { el.volume = Math.max(0, Math.min(1, v)); } catch { /* ignore */ }
      if (i >= steps) {
        window.clearInterval(id);
        this.fadeTimers.delete(el);
        onDone?.();
      }
    }, Math.max(20, Math.floor(ms / steps)));
    this.fadeTimers.set(el, id);
  }
}

export const musicController = new MusicController();